// microsoft-provider.js — Microsoft Graph (Outlook / Microsoft 365).
//
// Uses Graph's delta query (`/me/calendars/{id}/events/delta`) for
// incremental sync. The `@odata.deltaLink` returned at the end of a
// page-set is stored and replayed on subsequent fetches. Delta tokens
// expire after ~30 days of inactivity; on expiry Graph returns 410 and we
// drop the token and re-fetch from scratch.
//
// Credentials live in the workspace vault under `oauth:microsoft:<oid>`.
// Tenant is encoded in the token; we don't need to thread it through the
// REST calls (Graph routes via /me).
//
// REST endpoints used:
//   GET    /me/calendars                       — listCalendars
//   GET    /me/calendars/{id}/events/delta     — fetchEvents (incremental)
//   POST   /me/calendars/{id}/events           — createEvent
//   PATCH  /me/events/{id}                     — updateEvent (+ If-Match)
//   DELETE /me/events/{id}                     — deleteEvent (+ If-Match)

import { ProviderRegistry } from '../provider-registry.js';
import { ERROR_CODES } from './calendar-provider.js';
import { ICalCodec as Codec } from './ical-codec.js';
import { OAuthCalendarProvider } from './oauth-provider.js';
import { CalendarTransport as Transport } from './transport.js';

const API_BASE = 'https://graph.microsoft.com/v1.0';

export class MicrosoftProvider extends OAuthCalendarProvider {
  /** @param {any} [opts] */
  constructor(opts: any) {
    super({
      ...opts,
      id: 'microsoft',
      capabilities: { etag: true, rrule: true, delta: true, ...(opts?.capabilities || {}) },
    });
    this._token = null;
    this._deltaLinks = new Map(); // calendarId → deltaLink URL
  }

  /** @returns {Promise<any[]>} */
  async listCalendars() {
    const resp = await Transport.request({
      url: `${API_BASE}/me/calendars`,
      method: 'GET',
      ...(await this._requestOpts()),
    });
    const data = await resp.json();
    return (data.value || []).map((c: any) => ({
      id: c.id,
      title: c.name,
      color: c.hexColor || null,
      writable: c.canEdit !== false,
      defaultCalendar: !!c.isDefaultCalendar,
    }));
  }

  /**
   * @param {any} opts - { calendarId, startDate, endDate }
   * @returns {Promise<any>}
   */
  async fetchEvents({ calendarId, startDate, endDate }: any): Promise<any> {
    // Prefer stored delta link; otherwise initiate a fresh delta query.
    let url = this._deltaLinks.get(calendarId);
    if (!url) {
      const params = new URLSearchParams();
      if (startDate) params.set('startDateTime', new Date(startDate).toISOString());
      if (endDate) params.set('endDateTime', new Date(endDate).toISOString());
      url = `${API_BASE}/me/calendars/${encodeURIComponent(calendarId)}/calendarView/delta?${params}`;
    }
    const all = [];
    const deletedIds = [];
    // Validate/refresh the token (vault read + AES-GCM decrypt) ONCE per fetch
    // rather than once per delta page — a multi-page calendar otherwise
    // re-decrypts the same token on every page. A mid-fetch expiry is covered
    // by transport.ts's reactive 401 -> refresh, which mutates
    // reqOpts.credentials.token in place for subsequent pages.
    const reqOpts = await this._requestOpts();
    while (url) {
      let resp: any;
      try {
        resp = await Transport.request({
          url,
          method: 'GET',
          headers: { Prefer: 'odata.maxpagesize=200, outlook.timezone="UTC"' },
          ...reqOpts,
        });
      } catch (e: any) {
        if (e?.status === 410) {
          // delta token expired → reset and try fresh
          this._deltaLinks.delete(calendarId);
          return this.fetchEvents({ calendarId, startDate, endDate });
        }
        throw e;
      }
      const data = await resp.json();
      for (const item of data.value || []) {
        if (item['@removed']) {
          deletedIds.push(item.id);
          continue;
        }
        // An organiser-cancelled meeting stays in the attendee's calendarView
        // as a normal (non-@removed) resource with isCancelled:true until the
        // attendee deletes it. Treat it as a removal so it doesn't import as a
        // live event (mirrors Google's status==='cancelled' handling).
        if (item.isCancelled) {
          deletedIds.push(item.id);
          continue;
        }
        all.push(this._normalise(item, calendarId));
      }
      url = data['@odata.nextLink'] || null;
      if (data['@odata.deltaLink']) this._deltaLinks.set(calendarId, data['@odata.deltaLink']);
    }
    return { events: all, deletedIds };
  }

  /**
   * @param {any} item - a Graph event resource
   * @param {string} calendarId
   * @returns {any} the normalised event.
   */
  _normalise(item: any, calendarId: any) {
    const isAllDay = !!item.isAllDay;
    const startIso = item.start?.dateTime
      ? new Date(item.start.dateTime + (item.start.timeZone === 'UTC' ? 'Z' : '')).toISOString()
      : null;
    const endIso = item.end?.dateTime
      ? new Date(item.end.dateTime + (item.end.timeZone === 'UTC' ? 'Z' : '')).toISOString()
      : null;
    const rrule = item.recurrence ? _patternToRrule(item.recurrence) : null;
    return {
      providerId: item.id,
      accountId: this.accountId,
      calendarId,
      title: item.subject || '',
      description: item.body?.content || '',
      location: item.location?.displayName || '',
      startDate: startIso,
      endDate: endIso,
      allDay: isAllDay,
      rrule,
      recurrenceId: item.seriesMasterId ? item.originalStart || startIso : null,
      masterProviderId: item.seriesMasterId || null,
      etag: item['@odata.etag'] || null,
      sequence: item.changeKey ? 1 : 0,
      lastModified: item.lastModifiedDateTime,
    };
  }

  /**
   * @param {any} event - a normalised event
   * @returns {any} a Graph event resource.
   */
  _toGraphEvent(event: any) {
    const out: any = {};
    if (event.title != null) out.subject = event.title;
    if (event.description != null) out.body = { contentType: 'text', content: event.description };
    if (event.location != null) out.location = { displayName: event.location };
    out.isAllDay = !!event.allDay;
    const tz = 'UTC';
    if (event.allDay) {
      // Graph requires midnight + UTC for all-day events.
      out.start = {
        dateTime: `${new Date(event.startDate).toISOString().slice(0, 10)}T00:00:00.0000000`,
        timeZone: tz,
      };
      out.end = {
        dateTime: `${new Date(event.endDate).toISOString().slice(0, 10)}T00:00:00.0000000`,
        timeZone: tz,
      };
    } else {
      out.start = { dateTime: new Date(event.startDate).toISOString().slice(0, -1), timeZone: tz };
      out.end = { dateTime: new Date(event.endDate).toISOString().slice(0, -1), timeZone: tz };
    }
    if (event.rrule) out.recurrence = _rruleToPattern(event.rrule);
    return out;
  }

  /**
   * @param {string} calendarId
   * @param {any} event
   * @returns {Promise<any>}
   */
  async createEvent(calendarId: any, event: any) {
    const resp = await Transport.request({
      url: `${API_BASE}/me/calendars/${encodeURIComponent(calendarId)}/events`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(this._toGraphEvent(event)),
      ...(await this._requestOpts()),
    });
    const data = await resp.json();
    return {
      providerId: data.id,
      etag: data['@odata.etag'],
      lastModified: data.lastModifiedDateTime,
    };
  }

  /**
   * @param {string} _calendarId
   * @param {string} providerId
   * @param {any} event
   * @param {any} [opts]
   * @returns {Promise<any>}
   */
  async updateEvent(_calendarId: any, providerId: any, event: any, { etag } = {} as any) {
    const headers = { 'Content-Type': 'application/json' };
    if (etag) (headers as any)['If-Match'] = etag;
    const resp = await Transport.request({
      url: `${API_BASE}/me/events/${encodeURIComponent(providerId)}`,
      method: 'PATCH',
      headers,
      body: JSON.stringify(this._toGraphEvent(event)),
      ...(await this._requestOpts()),
    });
    const data = await resp.json();
    return { etag: data['@odata.etag'], lastModified: data.lastModifiedDateTime };
  }

  /**
   * @param {string} _calendarId
   * @param {string} providerId
   * @param {any} [opts]
   * @returns {Promise<void>}
   */
  async deleteEvent(_calendarId: any, providerId: any, { etag } = {} as any) {
    const headers = {};
    if (etag) (headers as any)['If-Match'] = etag;
    try {
      await Transport.request({
        url: `${API_BASE}/me/events/${encodeURIComponent(providerId)}`,
        method: 'DELETE',
        headers,
        ...(await this._requestOpts()),
      });
    } catch (e: any) {
      if (e?.code === ERROR_CODES.NOT_FOUND) return;
      throw e;
    }
  }
}

// ── recurrencePattern ↔ RRULE conversion ─────────────────────────────────
// Graph models recurrence as a structured object. We cover the common cases
// (daily, weekly with BYDAY, monthly absolute) — exotic patterns fall back
// to a plain weekly RRULE so we never lose the event.
/**
 * @param {any} rec - a Graph recurrence object
 * @returns {string | null} the equivalent RRULE, or null.
 */
function _patternToRrule(rec: any) {
  if (!rec?.pattern) return null;
  const { pattern, range } = rec;
  const parts = [];
  if (pattern.type === 'daily') parts.push('FREQ=DAILY');
  else if (pattern.type === 'weekly') {
    parts.push('FREQ=WEEKLY');
    if (pattern.daysOfWeek?.length) {
      const map = {
        sunday: 'SU',
        monday: 'MO',
        tuesday: 'TU',
        wednesday: 'WE',
        thursday: 'TH',
        friday: 'FR',
        saturday: 'SA',
      };
      parts.push(
        `BYDAY=${pattern.daysOfWeek
          .map((d: any) => (map as any)[d.toLowerCase()])
          .filter(Boolean)
          .join(',')}`
      );
    }
  } else if (pattern.type === 'absoluteMonthly') {
    parts.push('FREQ=MONTHLY');
    if (pattern.dayOfMonth) parts.push(`BYMONTHDAY=${pattern.dayOfMonth}`);
  } else if (pattern.type === 'absoluteYearly') {
    parts.push('FREQ=YEARLY');
  } else {
    parts.push('FREQ=WEEKLY');
  }
  if (pattern.interval && pattern.interval > 1) parts.push(`INTERVAL=${pattern.interval}`);
  if (range?.type === 'numbered' && range.numberOfOccurrences)
    parts.push(`COUNT=${range.numberOfOccurrences}`);
  if (range?.type === 'endDate' && range.endDate)
    // Graph's recurrenceRange.endDate is the INCLUSIVE last day the pattern
    // applies. Use end-of-day UTC so a timed occurrence on that date (e.g.
    // 09:00Z) still satisfies expandRrule's `ts <= untilTs` check — midnight
    // (T000000Z) would drop the final occurrence.
    parts.push(`UNTIL=${range.endDate.replace(/-/g, '')}T235959Z`);
  return parts.join(';');
}

/**
 * @param {string} rrule
 * @returns {any} the equivalent Graph recurrence object.
 */
function _rruleToPattern(rrule: any) {
  const map = Codec._rruleParts(rrule);
  const out: any = { pattern: {}, range: { type: 'noEnd' } };
  if (map.FREQ === 'DAILY') out.pattern.type = 'daily';
  else if (map.FREQ === 'WEEKLY') {
    out.pattern.type = 'weekly';
    if (map.BYDAY) {
      const rev = {
        SU: 'sunday',
        MO: 'monday',
        TU: 'tuesday',
        WE: 'wednesday',
        TH: 'thursday',
        FR: 'friday',
        SA: 'saturday',
      };
      out.pattern.daysOfWeek = map.BYDAY.split(',')
        .map((d: any) => (rev as any)[d])
        .filter(Boolean);
    }
  } else if (map.FREQ === 'MONTHLY') {
    out.pattern.type = 'absoluteMonthly';
    if (map.BYMONTHDAY) out.pattern.dayOfMonth = parseInt(map.BYMONTHDAY, 10);
  } else if (map.FREQ === 'YEARLY') {
    out.pattern.type = 'absoluteYearly';
  } else out.pattern.type = 'weekly';
  out.pattern.interval = map.INTERVAL ? parseInt(map.INTERVAL, 10) : 1;
  if (map.COUNT) out.range = { type: 'numbered', numberOfOccurrences: parseInt(map.COUNT, 10) };
  if (map.UNTIL) {
    const d = map.UNTIL.slice(0, 8);
    out.range = { type: 'endDate', endDate: `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}` };
  }
  return out;
}

// Phase 4.7: explicit ESM registration (import-driven ordering — see
// caldav-provider.js).
ProviderRegistry.register('microsoft', MicrosoftProvider);
